import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
    Form, 
    Input, 
    Button, 
    Card, 
    Space, 
    Select, 
    Typography, 
    Breadcrumb,
    InputNumber,
    Spin,
    Alert
} from 'antd';
import { 
    SaveOutlined, 
    ArrowLeftOutlined,
    BankOutlined
} from '@ant-design/icons';
import { useTransaccs } from '../hooks/useTransaccs';
import {CoaDTO,TransaccDTO} from "@/shared/api/model";

const { Title } = Typography;
const { Option } = Select;

const TransaccFormView: React.FC = () => {
    const navigate = useNavigate();
    const { glNo, acNo } = useParams<{ glNo: string, acNo: string }>();
    const [form] = Form.useForm();
    const isEditMode = !!glNo && !!acNo;
    
    const { 
        transaccs, 
        coaLeafNodes,
        isLoading, 
        isCreating, 
        isUpdating,
        createTransacc, 
        updateTransacc,
        getAcctypeLabel
    } = useTransaccs();
    
    const [selectedTransacc, setSelectedTransacc] = useState<TransaccDTO | null>(null);
    const [selectedGlNo, setSelectedGlNo] = useState<string | null>(null);
    
    // Find the transacc to edit if in edit mode
    useEffect(() => {
        if (isEditMode && transaccs.length > 0) {
            const foundTransacc = transaccs.find(
                item => item.glNo === glNo && item.acNo === acNo
            );
            
            if (foundTransacc) {
                setSelectedTransacc(foundTransacc);
                form.setFieldsValue({
                    glNo: foundTransacc.glNo,
                    acNo: foundTransacc.acNo,
                    acTitle: foundTransacc.acTitle,
                    acctype: foundTransacc.acctype
                });
            }
        }
    }, [isEditMode, transaccs, glNo, acNo, form]);
    
    // Handle form submission
    const handleSubmit = (values: any) => {
        const transaccData: TransaccDTO = {
            glNo: values.glNo,
            acNo: values.acNo || '',
            acTitle: values.acTitle,
            acctype: values.acctype
        };
        
        if (isEditMode && selectedTransacc) {
            updateTransacc(transaccData);
        } else {
            createTransacc(transaccData);
        }
        
        // Navigate back to the list view after submission
        navigate('/accounts/transacc');
    };
    
    // Handle cancellation
    const handleCancel = () => {
        navigate('/accounts/transacc');
    };
    
    // Validate account number to ensure it only contains numbers when provided
    const validateAccountNumber = (_: any, value: string) => {
        if (!value) {
            return Promise.resolve();
        }
        
        if (!/^\d+$/.test(value?.trim())) {
            return Promise.reject(new Error('Account number must contain only numbers'));
        }
        
        return Promise.resolve();
    };
    
    return (
        <div className="p-4">
            <Breadcrumb className="mb-4">
                <Breadcrumb.Item>Home</Breadcrumb.Item>
                <Breadcrumb.Item>Accounts</Breadcrumb.Item>
                <Breadcrumb.Item>
                    <a onClick={handleCancel}>Bank/Cash/Transfer Accounts</a>
                </Breadcrumb.Item>
                <Breadcrumb.Item>{isEditMode ? 'Edit' : 'Create'} Account</Breadcrumb.Item>
            </Breadcrumb>
            
            <Card>
                <div className="flex justify-between items-center mb-4">
                    <Title level={4}>
                        <Space>
                            <BankOutlined />
                            {isEditMode ? 'Edit' : 'Create'} Bank/Cash/Transfer Account
                        </Space>
                    </Title>
                </div>
                
                {isLoading ? (
                    <div className="flex justify-center items-center p-8">
                        <Spin size="large" />
                    </div>
                ) : (
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleSubmit}
                        initialValues={{
                            acctype: 1 // Default to Bank account
                        }}
                    >
                        <Form.Item
                            name="glNo"
                            label="GL Number"
                            rules={[{ required: true, message: 'Please select a GL number' }]}
                        >
                            <Select 
                                placeholder="Select GL Number"
                                showSearch
                                optionFilterProp="children"
                                disabled={isEditMode}
                                onChange={(value) => setSelectedGlNo(value)}
                            >
                                {coaLeafNodes.map((node: CoaDTO) => (
                                    <Option key={node.glNo} value={node.glNo}>
                                        {node.glNo} - {node.acName}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                        
                        <Form.Item
                            name="acNo"
                            label="Account Number (Optional, numbers only)"
                            rules={[
                                { validator: validateAccountNumber }
                            ]}
                        >
                            <Input placeholder="Enter account number (optional)" disabled={isEditMode} />
                        </Form.Item>
                        
                        <Form.Item
                            name="acTitle"
                            label="Account Title"
                            rules={[{ required: true, message: 'Please enter account title' }]}
                        >
                            <Input placeholder="Enter account title" />
                        </Form.Item>
                        
                        <Form.Item
                            name="acctype"
                            label="Account Type"
                            rules={[{ required: true, message: 'Please select account type' }]}
                        >
                            <Select placeholder="Select account type">
                                <Option value={0}>Cash</Option>
                                <Option value={1}>Bank</Option>
                                <Option value={2}>Transfer</Option>
                            </Select>
                        </Form.Item>
                        
                        <Form.Item>
                            <Space>
                                <Button 
                                    type="primary" 
                                    htmlType="submit"
                                    icon={<SaveOutlined />}
                                    loading={isCreating || isUpdating}
                                >
                                    {isEditMode ? 'Update' : 'Create'}
                                </Button>
                                <Button 
                                    icon={<ArrowLeftOutlined />}
                                    onClick={handleCancel}
                                >
                                    Cancel
                                </Button>
                            </Space>
                        </Form.Item>
                    </Form>
                )}
            </Card>
        </div>
    );
};

export default TransaccFormView;
